import { Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import React from 'react';
import {
    ComposedChart,
    Line,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';

const ConsumptionMonthlyChart = ({
    consumptionData,
    isLoading,
    selectedUnit,
    handleUnitChange,
    yearsList,
    selectedYear,
    selectedMonth,
    onYearChange,
    onMonthChange,
    months
}) => {
    const transformedData = transformData(consumptionData);

    return (
        <>
            <Box marginBottom={`14px`} display={'flex'}>
                <FormControl
                    size="small"
                    sx={{
                        minWidth: 'min-content',
                        marginRight: '28px'
                    }}
                >
                    <InputLabel
                        id="demo-simple-select-label"
                        sx={{
                            fontSize: '12px',
                            marginTop: '5px'
                        }}
                    >
                        Year
                    </InputLabel>
                    <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        label="Year"
                        onChange={onYearChange}
                        value={selectedYear}
                        defaultValue={selectedYear}
                        sx={{
                            fontSize: '12px',
                            minWidth: '96px',
                            maxWidth: 'min-content'
                        }}
                    >
                        {yearsList.map((item, index) => (
                            <MenuItem
                                key={index}
                                value={item}
                                sx={{
                                    fontSize: '12px'
                                }}
                            >
                                {item}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl
                    size="small"
                    sx={{
                        minWidth: 'min-content',
                        marginRight: '28px'
                    }}
                >
                    <InputLabel
                        id="demo-simple-select-label"
                        sx={{
                            fontSize: '12px',
                            marginTop: '5px'
                        }}
                    >
                        Month
                    </InputLabel>
                    <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        label="Month"
                        onChange={onMonthChange}
                        value={selectedMonth}
                        defaultValue={selectedMonth}
                        sx={{
                            fontSize: '12px',
                            minWidth: '96px',
                            maxWidth: 'min-content'
                        }}
                    >
                        {months.map((item, index) => (
                            <MenuItem
                                key={index}
                                value={index}
                                sx={{
                                    fontSize: '12px'
                                }}
                            >
                                {item}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small">
                    <InputLabel
                        id="demo-simple-select-label"
                        sx={{
                            fontSize: '12px',
                            marginTop: '5px'
                        }}
                    >
                        Unit
                    </InputLabel>
                    <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        label="Unit"
                        onChange={handleUnitChange}
                        value={selectedUnit}
                        defaultValue={selectedUnit}
                        sx={{
                            fontSize: '12px',
                            minWidth: '96px',
                            maxWidth: 'min-content'
                        }}
                    >
                        <MenuItem
                            value={'mw'}
                            sx={{
                                fontSize: '12px'
                            }}
                        >
                            MW
                        </MenuItem>
                        <MenuItem
                            value={'mwh'}
                            sx={{
                                fontSize: '12px'
                            }}
                        >
                            MWh
                        </MenuItem>
                    </Select>
                </FormControl>
            </Box>
            {isLoading ? (
                <p className="h-[90%] flex items-center justify-center">
                    Loading....
                </p>
            ) : (
                <ResponsiveContainer width="100%" height="95%">
                    <ComposedChart
                        width={500}
                        height={400}
                        data={transformedData}
                    >
                        <CartesianGrid stroke="#f5f5f5" />
                        <XAxis dataKey="name" type="category" fontSize={12} />
                        <YAxis
                            fontSize={12}
                            tickCount={10}
                            interval="preserveEnd"
                        />
                        <Tooltip />
                        <Legend />
                        <Bar
                            stackId={'a'}
                            type="monotone"
                            dataKey="Year"
                            fill="#8BC1F7"
                            order={1}
                        />
                        <Bar
                            stackId={'a'}
                            type="monotone"
                            dataKey="Month"
                            fill="#7CC674"
                            order={2}
                        />
                        <Bar
                            stackId={'a'}
                            type="monotone"
                            dataKey="Quarter"
                            fill="#73C5C5"
                            order={3}
                        />
                        <Bar
                            stackId={'a'}
                            type="monotone"
                            dataKey="Others"
                            fill="#8481DD"
                            order={4}
                        />
                        <Bar
                            stackId={'a'}
                            type="monotone"
                            dataKey="GY"
                            fill="#F4C145"
                            order={5}
                        />{' '}
                        <Bar
                            stackId={'a'}
                            type="monotone"
                            dataKey="Summer"
                            fill="#EC7A08"
                            order={6}
                        />
                        <Bar
                            stackId={'a'}
                            type="monotone"
                            dataKey="Winter"
                            fill="#B8BBBE"
                        />
                        <Line
                            dataKey="Demand"
                            stroke="#05c46b"
                            strokeWidth={3}
                            connectNulls
                        />
                        <Line
                            dataKey="Demand 2"
                            stroke="#ffa801"
                            strokeWidth={3}
                            connectNulls
                        />
                        <Line
                            dataKey="Demand 3"
                            stroke="#3c40c6"
                            strokeWidth={3}
                            connectNulls
                        />
                        <Line
                            dataKey="Demand 4"
                            stroke="#0652DD"
                            strokeWidth={3}
                            connectNulls
                        />
                        <Line
                            dataKey="Demand 5"
                            stroke="#B53471"
                            strokeWidth={3}
                            connectNulls
                        />
                        <Line
                            dataKey="MEV"
                            stroke="#ff7300"
                            strokeWidth={3}
                            connectNulls
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            )}
        </>
    );
};

export default ConsumptionMonthlyChart;

function transformData(datasets) {
    const transformedData = [];

    // Get all the unique x-values across all datasets
    const uniqueXValues = new Set();
    datasets.forEach((dataset) => {
        dataset.data.forEach(([x]) => {
            uniqueXValues.add(x);
        });
    });

    // Create an object for each unique x-value
    [...uniqueXValues].forEach((xValue) => {
        const dataPoint = { name: xValue.toString() };

        // Populate the dataset values for this x-value
        datasets.forEach((dataset) => {
            const { name: datasetName, data } = dataset;
            const yValue = data.find(([x]) => x === xValue)?.[1] || 0; // Find the y-value for this x-value, or use 0 if not found
            dataPoint[datasetName] = yValue;
        });

        transformedData.push(dataPoint);
    });

    return transformedData;
}
